import { Component, Host, h, Element, Prop, Listen } from '@stencil/core';

@Component({
  tag: 'vds-teaser-solutions',
  styleUrl: 'vds-teaser-solutions.scss',
  shadow: true,
})
export class VdsTeaserSolutions {

  @Element() el!: HTMLElement;

  /**
   * Headline for the Content Modules
   * Mandatory: no
   * Max Chars: 50
   */
  @Prop() headline: string;

  /**
   * Subline for the Content Module
   * Mandatory: no
   * Max Chars: 150
   */
  @Prop() subline: string;

  /**
   * Id for connection between the pagination and slides elements
   */
  @Prop() slidesId: string;

  /**
   * Max value for the pagination element
   */
  @Prop({ mutable: true }) paginationMax: number;

  /**
   * Disables sliding
   */
  @Prop() disableSlides: boolean = false;

  /**
   * Sets displayed layout (2 or 3 items per view)
   */
  @Prop() layout: 'halves' | 'thirds' = 'thirds';

  /**
   * Controls the space between items
   */
  @Prop() spacing: 'default' | 'narrow' = 'default';

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.setupSlides();
    this.setPaginationMax();
  }

  render() {
    return (
      <Host>

        <h2 class="font-heavy text-h2-mobile sm:text-h2-tablet lg:text-h2-desktop text-grey uppercase break-words" innerHTML={this.headline}></h2>
        <h3 class="font-regular text-s2-mobile sm:text-s2-tablet lg:text-s2-desktop text-berry uppercase mt-2 mb-6 break-words" innerHTML={this.subline}></h3>

        <slot name="slides"></slot>

        <div
          class={{
            "flex justify-center pt-[30px]": true,
            "pb-[30px]": this.paginationMax <= 1 || this.disableSlides,
          }}>
          <vds-pagination-progress
            class={{ "hidden": this.disableSlides }}
            max={this.paginationMax}
            slides-ref={"#" + this.slidesId}>
          </vds-pagination-progress>
        </div>

      </Host>
    );
  }

  async connectedCallback(): Promise<void> {
    this.setSlidesId();
  }

  async componentDidRender(): Promise<void> {
    this.setupSlides();
    this.setPaginationMax();
  }

  private setSlidesId(): void {
    const slidesEl = this.el.querySelector('vds-slides');
    slidesEl.id = this.slidesId;
  }

  private setupSlides(): void {
    const slidesEl = this.el.querySelector('vds-slides');
    const slideEl = this.el.querySelectorAll('vds-slide');
    const windowWidth = window.innerWidth;

    slidesEl.disableCentering();

    if(this.disableSlides) {
      slidesEl.lockSwipes(true);
    }

    if(windowWidth >= 1200) {
      if(this.spacing == 'narrow') {
        slidesEl.setSpaceBetween(30);
      }
      else {
        slidesEl.setSpaceBetween(50);
      }
    }
    else {
      slidesEl.setSpaceBetween(30);
    }

    slideEl.forEach((x) => {
      x.style.width = `auto`;
    });

    if(this.layout == "thirds") {
      if(windowWidth >= 960) {
        slidesEl.setSlidesPerView(3);
      }
      if(windowWidth >= 640 && windowWidth < 960) {
        slidesEl.setSlidesPerView(2);
      }
    }
    else {
      if(windowWidth >= 640) {
        slidesEl.setSlidesPerView(2);
      }
    }

    if(windowWidth < 640) {
      slidesEl.setSlidesPerView(1);
    }
  }

  private setPaginationMax(): void {
    const slideCount = this.el.querySelectorAll('vds-slide').length;
    const windowWidth = window.innerWidth;

    if (this.layout == "halves") {
      if (windowWidth >= 640) {
        this.paginationMax = slideCount - 1;
      }
      else if (windowWidth < 640) {
        this.paginationMax = slideCount;
      }
    }

    if (this.layout == "thirds") {
      if (windowWidth >= 960) {
        this.paginationMax = slideCount - 2;
      }
      else if (windowWidth >= 640 && windowWidth < 960) {
        this.paginationMax = slideCount - 1;
      }
      else if (windowWidth < 640) {
        this.paginationMax = slideCount;
      }
    }
  }

}
